import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { X, Mail, Lock, User as UserIcon, Loader2 } from "lucide-react";
import { registerApi, loginApi } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

function extractErr(e) {
  // Network / CORS / no response
  if (!e?.response) {
    if (e?.message?.includes("Network")) return "Couldn't reach the server. Check your internet and try again.";
    return e?.message || "Couldn't reach the server. Try again.";
  }
  const d = e.response?.data?.detail;
  const status = e.response?.status;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(x => x?.msg).filter(Boolean).join(" · ") || `Error ${status}`;
  if (d && typeof d === "object" && typeof d.msg === "string") return d.msg;
  if (status === 429) return "Too many attempts. Try again in 15 minutes.";
  if (status === 409) return "An account with this email already exists.";
  if (status >= 500) return "Server error. Please try again in a moment.";
  return `Something went wrong (${status || "?"}). Please try again.`;
}

export default function AuthModal({ open, onClose, initialMode = "signin" }) {
  const [mode, setMode] = useState(initialMode); // 'signin' | 'signup'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const { setUser, refresh } = useAuth();
  const nav = useNavigate();

  const handleGoogle = () => {
    const redirectUrl = `${window.location.origin}/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = mode === "signup"
        ? await registerApi({ email: email.trim(), password, name: name.trim() })
        : await loginApi({ email: email.trim(), password });
      if (!res?.user) throw new Error("No user returned");
      // Set immediately for snappy UI
      setUser(res.user);
      // CRITICAL: re-fetch via cookie to guarantee the session sticks across the
      // navigation. If the cookie wasn't set (CORS / SameSite / network blip),
      // refresh() returns null and we keep the modal open with an error rather
      // than bouncing the user back to Landing via ProtectedRoute.
      const verified = await refresh();
      if (!verified) {
        throw new Error("Session didn't persist. Please try again or use Continue with Google.");
      }
      toast.success(mode === "signup" ? "Welcome to Fanpuff!" : "Signed in.");
      onClose?.();
      // Reset tour seen flag for new signups so QuickTour auto-pops on Dashboard
      if (mode === "signup") {
        try { localStorage.removeItem("fanpuff_tour_seen"); } catch (e) { /* noop */ }
      }
      if (!verified.age_verified || !verified.region) {
        nav("/welcome", { replace: true, state: { user: verified } });
      } else {
        nav("/dashboard", { replace: true, state: { user: verified } });
      }
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 grid place-items-center p-4"
          data-testid="auth-modal"
        >
          <div className="absolute inset-0 bg-black/85 backdrop-blur-md" onClick={busy ? undefined : onClose} />
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="relative w-full max-w-md glass-strong rounded-3xl p-6 border border-white/10"
          >
            <button
              onClick={onClose}
              disabled={busy}
              className="absolute top-4 right-4 w-8 h-8 rounded-full grid place-items-center text-white/60 hover:text-white hover:bg-white/10"
              data-testid="auth-close"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-[0.3em] font-bold" style={{ color: "#FF0055" }}>Fanpuff</span>
            </div>
            <h2 className="font-display text-2xl font-bold mt-1">
              {mode === "signup" ? "Create your account" : "Welcome back"}
            </h2>
            <p className="text-sm text-white/60 mt-1">
              {mode === "signup" ? "Join the culture. 1,00,000 free Fanpuff Credits waiting." : "Sign in to pick up your Circle."}
            </p>

            {/* Google button */}
            <button
              onClick={handleGoogle}
              disabled={busy}
              data-testid="auth-google-btn"
              className="mt-5 w-full py-3 rounded-full font-bold bg-white text-black inline-flex items-center justify-center gap-2 hover:bg-white/90 transition"
            >
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
              </svg>
              Continue with Google
            </button>

            <div className="my-5 flex items-center gap-3">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-[10px] tracking-widest uppercase text-white/40 font-bold">or</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>

            <form onSubmit={submit} className="space-y-3">
              {mode === "signup" && (
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    autoComplete="name"
                    required
                    data-testid="auth-name"
                    className="w-full pl-10 pr-3 py-3 rounded-xl bg-white/5 border border-white/10 text-sm focus:outline-none focus:border-[#FF0055]/60"
                  />
                </div>
              )}
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  autoComplete="email"
                  required
                  data-testid="auth-email"
                  className="w-full pl-10 pr-3 py-3 rounded-xl bg-white/5 border border-white/10 text-sm focus:outline-none focus:border-[#FF0055]/60"
                />
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={mode === "signup" ? "Password (min 8 chars)" : "Password"}
                  autoComplete={mode === "signup" ? "new-password" : "current-password"}
                  required
                  minLength={mode === "signup" ? 8 : undefined}
                  data-testid="auth-password"
                  className="w-full pl-10 pr-3 py-3 rounded-xl bg-white/5 border border-white/10 text-sm focus:outline-none focus:border-[#FF0055]/60"
                />
              </div>

              <button
                type="submit"
                disabled={busy}
                data-testid="auth-submit"
                className="w-full py-3 rounded-full font-bold text-black inline-flex items-center justify-center gap-2"
                style={{ background: "linear-gradient(90deg,#FF0055,#B026FF)" }}
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {mode === "signup" ? "Create account" : "Sign in"}
              </button>
            </form>

            <p className="text-center text-xs text-white/55 mt-4">
              {mode === "signup" ? (
                <>Already have an account?{" "}
                  <button onClick={() => setMode("signin")} data-testid="auth-switch-signin" className="text-white font-bold hover:underline">Sign in</button>
                </>
              ) : (
                <>New to Fanpuff?{" "}
                  <button onClick={() => setMode("signup")} data-testid="auth-switch-signup" className="text-white font-bold hover:underline">Create account</button>
                </>
              )}
            </p>

            <p className="text-center text-[10px] text-white/35 mt-3">
              By continuing you agree to our Terms & Privacy. 18+ only. No real money.
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
